import {Selector} from "../mixins/dictionaries";
import {paddingOligoEditor as paddingEditor} from "../mixins/constants.js"
import {getRangeBases, textSize, fixedMaxValue} from "./utils.js";


import {line} from "d3-shape";
import {drag} from "d3-drag";
import {select, selectAll} from "d3-selection";

const d3 = {select, selectAll, drag, line}

import {
    editorMixins,
    brushMixins,
} from "../mixins/oligoMixins.js";
import {dnaClassColor, isInRange, isInsideArea, scaleAnnotationSize} from "../editor/utils";
import {annotationPath} from "../mixins/linearShape";

export default class RawEditor {
    constructor(settings, mediator) {
        this.settings = {
            ...settings
        }
        Object.assign(
            this,
            editorMixins,
            brushMixins
        );

        this.mediator = mediator;
        this.initDataContainer();
        this.initEditor(Selector.RAW_EDITOR);
    }

    initDataContainer() {
        this.$activeContainer = document.querySelector("#editor .tab-pane.active");
        this.fixedFontSize = 1;

        this.offsetScroll = {
            "current": 0,
            "step": 0,
        }

        this.boundaries = {
            "left": 0,
            "right": 0,
        }

        this.regularSizeFont = textSize("ACGTACGTACGT", this.$activeContainer, this.fixedFontSize);
        const [fontWidth, fontHeight] = [this.regularSizeFont.width, this.regularSizeFont.height];
        this.fontBoxWidth = fontWidth / 12;
        this.fontSizeBase = fontWidth / 12;
        this.sourceObject = "raw";
        this.slots = [];
        this.slotFriendlyId = null;
    }

    initData(decodedStructuredData, sourceData) {
        this.sequenceMapped = decodedStructuredData.sequence;
        this.rawSequenceMapIndexes = decodedStructuredData["decodedRawSequenceMapIndexes"];
        this.characters = decodedStructuredData["decodedCartoonMap"];
        this.onlyCharacters = decodedStructuredData["decodedCartoonMap"].filter(item => !item.bond);

        this.crossMap = decodedStructuredData["decodedCrossMap"];
        this.charactersLength = this.onlyCharacters.length;
        this.sequenceLength = this.sequenceMapped.length;

        this.slots = sourceData.slots;

        // set init friendlyID
        if (!this.slotFriendlyId) {
            this.slotFriendlyId = sourceData.friendly_id;
            for (let i = 0; i < this.slots.length; i++) {
                if (this.slots[i].length) {
                    this.slotFriendlyId = this.slots[i].dna_sequence.friendly_id;
                    break;
                }
            }
        }
    }

    createTicks() {
        let position;

        this.rulerContainer.selectAll(".tickGroup").remove();

        /**
         * draw first tick if inside boundaries range
         */
        const firstBase = this.onlyCharacters[0];
        if (firstBase.charIndex >= this.boundaries.left && firstBase.charIndex < this.boundaries.right) {
            position = firstBase.rawStart + paddingEditor + (firstBase.typeMap.raw_symbol.length / 2);
            this.renderTick(1, position);
        }

        // intermediate Ticks (multiples of 5, except 0)
        for (let index = 4; index < this.onlyCharacters.length; index += 5) {
            const base = this.onlyCharacters[index];
            if (base.rawStart > this.boundaries.left && base.rawStart < this.boundaries.right) {
                position = base.rawStart + paddingEditor + (base.typeMap.raw_symbol.length / 2);
                this.renderTick(index + paddingEditor, position - this.offsetScroll.current);
            }
        }
    }

    renderTick(tickNumber, tickPosition) {
        const tickContainer = this.rulerContainer.append("g")
            .attr("class", "tickGroup")
            .attr("transform", "translate(" + (tickPosition * this.fontBoxWidth) + ",0)")

        tickContainer.append("line")
            .attr("class", "tickLine")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", 10)
            .attr("stroke", "#000")
            .attr("stroke-width", 1)
            .attr("opacity", 1);

        tickContainer.append("text")
            .attr("class", "tickText")
            .attr("dy", 20)
            .style("font-size", .7 + "em")
            .style('text-anchor', 'middle')
            .text(tickNumber)
    }

    renderSequence() {
        const bases = getRangeBases(this.boundaries.left, this.boundaries.right, this.sequenceMapped)
        this.sequenceContainer.selectAll("text.base").remove();

        this.sequenceContainer
            .attr("transform", `translate(${this.fontBoxWidth}, ${this.placements.editor.sequence})`)
            .selectAll("text.base")
            .data(bases)
            .enter()
            .append("g")
            .attr("transform", (d, index) => `translate(${index * this.fontBoxWidth}, 0)`)
            .each(function (d) {
                const group = d3.select(this)

                group.append("text")
                    .attr("class", "base font-monospace")
                    .attr("x", 0)
                    .attr("y", 0)
                    .attr("dy", 0)
                    .attr("fill", "#000")
                    .attr("text-anchor", "start")
                    .text(d);
            });
    }

    updateBases() {
        const bases = getRangeBases(this.boundaries.left, this.boundaries.right, this.sequenceMapped)
        const baseNodes = this.sequenceContainer.selectAll(".base").nodes();

        let count = 0;
        baseNodes.forEach(n => {
            n.innerHTML = bases[count];
            count++
        })
    }

    renderSlots() {
        const slotHeight = 20;
        let start = 0;
        let end = 0;

        this.slotsContainer
            .attr("transform", `translate(${-(this.offsetScroll.current * this.fontBoxWidth)}, ${this.placements.slots})`)
            .selectAll(".slot-group").remove();

        this.slots.forEach(s => {
            if (s.dna_sequence.encodings.length) {
                const lastEncoded = s.dna_sequence.encodings[s.dna_sequence.encodings.length - 1];
                end = this.crossMap.find(item => item.pk === lastEncoded.id).raw.end;

                const downStream_raw_Symbol = this.settings.type_map[s.downstream_linkage]?.raw_symbol;
                const downStreamLinkageLength = -(downStream_raw_Symbol?.length ?? 0);

                const slotLength = (end - start);

                if (slotLength && isInsideArea(start + paddingEditor, end + paddingEditor, this.boundaries.left, this.boundaries.right, this.sequenceLength)) {
                    const label = s.dna_sequence.name + " (" + s.dna_sequence.friendly_id + ")";
                    const container = this.slotsContainer
                        .append("g")
                        .attr("class", "slot-group slot-" + s.pk)
                        .attr('data-toggle', 'virtual-popper')
                        .attr("data-virtual-popper-content", label)
                        .attr("transform", `translate(${(start + paddingEditor) * this.fontBoxWidth},0)`)

                    this.drawSlot(container, s, slotLength + downStreamLinkageLength, s.direction, dnaClassColor(s.dna_sequence), slotHeight);
                }
                start = end;
            }
        })
    }


    alignRangeBp(brushSize, event) {
        let start = brushSize.x / this.fontBoxWidth;
        let end = start + (brushSize.width / this.fontBoxWidth);

        start = fixedMaxValue(start + this.offsetScroll.current, paddingEditor,this.sequenceLength + paddingEditor);
        end = fixedMaxValue(end + this.offsetScroll.current, paddingEditor,this.sequenceLength + paddingEditor);

        /**
         *
         * */
        if (start !== end) {
            let encodingEnd
            const encodingStart = this.rawSequenceMapIndexes.find(item => item.index === Math.round(start - paddingEditor));
            if (end <= this.sequenceLength) {
                encodingEnd = this.rawSequenceMapIndexes.find(item => item.index === Math.round(end - paddingEditor));
            } else {
                encodingEnd = {"start": this.sequenceLength}
            }

            start = Math.round(encodingStart.start + paddingEditor);
            end = Math.round(encodingEnd.start + paddingEditor);
        } else {
            // set position to start or end to the nearest token boundary via rawSequenceMapIndexes
            const raw = this.rawSequenceMapIndexes.find(item => item.index === Math.round(start - paddingEditor));
            if (raw) {
                start = end = raw.start + paddingEditor
            }
            this.EventsTrigger("setCurrentEncodingAnchor", {
                "currentPosition": start - paddingEditor + this.offsetScroll.current,
                "source": this.sourceObject
            })
        }

        this.rangeBp = {start, end};

        this.renderBrush(this.rangeBp.start, this.rangeBp.end, this.offsetScroll.current, this.fontBoxWidth);
    }

    scrollBrush() {
        const newBrushSize = {
            x: (this.rangeBp.start + this.offsetScroll.step) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize);
    }
}